import React, { useState, useEffect, useMemo } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { motion } from 'framer-motion';
import {
    FaEye,
    FaSearch,
    FaSort,
    FaSortUp,
    FaSortDown,
    FaFilter,
    FaMapMarkerAlt,
    FaImage,
    FaStickyNote
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacyBranchRequests = () => {
    const { setTitle } = useTitle();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [pageInput, setPageInput] = useState(1);
    const [pageGroup, setPageGroup] = useState(0);

    useEffect(() => {
        setTitle('طلبات الفروع');
        fetchRequests();
    }, [setTitle, currentPage]);

    // تحديث pageInput عند تغيير currentPage
    useEffect(() => {
        setPageInput(currentPage);
    }, [currentPage]);

    // دالة التنقل بالصفحات عبر input
    const handlePageInputChange = (e) => {
        const value = parseInt(e.target.value);
        if (!isNaN(value) && value >= 1) {
            setPageInput(value);
        }
    };

    const handlePageInputSubmit = (e) => {
        e.preventDefault();
        const totalPages = pagination?.total_pages || pagination?.last_page || 1;
        if (pageInput >= 1 && pageInput <= totalPages) {
            handlePageChange(pageInput);
        }
    };

    // دالة تغيير الصفحة
    const handlePageChange = (page) => {
        setCurrentPage(page);

        // تحديث pageGroup بناءً على الصفحة المختارة
        const totalPages = pagination?.total_pages || pagination?.last_page || 1;
        const pagesPerGroup = Math.ceil(totalPages / 2);
        const newPageGroup = Math.floor((page - 1) / pagesPerGroup);
        setPageGroup(newPageGroup);
    };

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    // دالة للحصول على لون ونص الحالة
    const getStatusInfo = (status) => {
        const statusMap = {
            'approved': {
                class: 'bg-success',
                text: 'موافق عليه'
            },
            'rejected': {
                class: 'bg-danger',
                text: 'مرفوض'
            },
            'pending': {
                class: 'bg-warning',
                text: 'في الانتظار'
            },
            'doctor_responded': {
                class: 'bg-info',
                text: 'رد الصيدلية'
            },
            'under_review': {
                class: 'bg-secondary',
                text: 'قيد المراجعة'
            },
            'completed': {
                class: 'bg-success',
                text: 'مكتمل'
            },
            'cancelled': {
                class: 'bg-dark',
                text: 'ملغي'
            }
        };

        return statusMap[status] || {
            class: 'bg-light text-dark',
            text: status || 'غير محدد'
        };
    };

    // Column helper for table configuration
    const columnHelper = createColumnHelper();

    // Table columns configuration
    const columns = useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: '#',
                cell: ({ row }) => (
                    <span className="fw-semibold text-muted">#{row.index + 1}</span>
                ),
                size: 60,
            }),
            columnHelper.accessor('client', {
                id: 'client',
                header: 'اسم العميل',
                cell: ({ getValue }) => (
                    <div className="text-end" style={{ paddingRight: '30px' }}>
                        <h6 className="mb-0 fw-semibold" style={{ fontSize: '12px' }}>
                            {getValue()?.name || (typeof getValue() === 'string' ? getValue() : 'غير محدد')}
                        </h6>
                    </div>
                ),
                size: 150,
            }),
            columnHelper.accessor('client_phone', {
                id: 'client_phone',
                header: 'رقم هاتف العميل',
                cell: ({ getValue }) => {
                    const phone = getValue();
                    return phone ? (
                        <a
                            href={`tel:${phone}`}
                            className="text-decoration-none"
                            style={{ color: '#184194', fontSize: '12px' }}
                            title="اتصال"
                        >
                            {phone}
                        </a>
                    ) : (
                        <span className="text-muted">غير محدد</span>
                    );
                },
                size: 140,
            }),
            columnHelper.accessor('branch.market_name', {
                id: 'branch_name',
                header: 'اسم الفرع',
                cell: ({ row }) => {
                    const branchName = row.original.branch?.market_name;
                    return branchName ? (
                        <div
                            className="text-center"
                            style={{
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                            title={branchName}
                        >
                            <span className="badge bg-primary text-white" style={{
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'inline-block'
                            }}>
                                {branchName}
                            </span>
                        </div>
                    ) : (
                        <span className="text-muted">غير محدد</span>
                    );
                },
                size: 300,
            }),
            columnHelper.accessor('branch.phone', {
                id: 'phone',
                header: 'هاتف الفرع',
                cell: ({ row }) => {
                    const phone = row.original.branch?.phone;
                    return phone ? (
                        <span className="text-muted" style={{ fontSize: '12px' }}>
                            {phone}
                        </span>
                    ) : (
                        <span className="text-muted">غير محدد</span>
                    );
                },
                size: 120,
            }),
            columnHelper.accessor('branch.email', {
                id: 'email',
                header: 'البريد الإلكتروني',
                cell: ({ row }) => {
                    const email = row.original.branch?.email;
                    return email ? (
                        <span className="text-muted" style={{ fontSize: '11px' }}>
                            {email}
                        </span>
                    ) : (
                        <span className="text-muted">غير محدد</span>
                    );
                },
                size: 150,
            }),
            columnHelper.accessor('branch.address', {
                id: 'address',
                header: 'العنوان',
                cell: ({ row }) => {
                    const address = row.original.branch?.address;
                    return address ? (
                        <span className="text-muted" style={{ fontSize: '11px' }}>
                            {address}
                        </span>
                    ) : (
                        <span className="text-muted">غير محدد</span>
                    );
                },
                size: 300,
            }),
            columnHelper.accessor('branch.lat', {
                id: 'coordinates',
                header: 'الإحداثيات',
                cell: ({ row }) => {
                    const lat = row.original.branch?.lat;
                    const lng = row.original.branch?.lng;
                    return lat && lng ? (
                        <div className="d-flex flex-column align-items-center gap-1">
                            <span className="text-muted" style={{ fontSize: '10px' }}>
                                {parseFloat(lat).toFixed(7)}, {parseFloat(lng).toFixed(7)}
                            </span>
                            <a
                                href={`https://www.google.com/maps?q=${parseFloat(lat).toFixed(7)},${parseFloat(lng).toFixed(7)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary"
                                style={{ fontSize: '9px', padding: '1px 4px' }}
                                title="فتح في خرائط جوجل"
                            >
                                <FaMapMarkerAlt className="mx-1" />
                                خرائط
                            </a>
                        </div>
                    ) : (
                        <span className="text-muted">غير محدد</span>
                    );
                },
                size: 150,
            }),
            columnHelper.accessor('images', {
                id: 'images',
                header: 'الصور',
                cell: ({ row }) => {
                    const images = row.original.images;
                    return images && images.length > 0 ? (
                        <div className="d-flex justify-content-center">
                            <img
                                src={images[0]}
                                alt="طلب"
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '6px',
                                    border: '1px solid #dee2e6'
                                }}
                                title={`${images.length} صورة`}
                            />
                            {images.length > 1 && (
                                <span className="badge bg-secondary ms-1" style={{ fontSize: '10px' }}>
                                    +{images.length - 1}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-muted">لا توجد صور</span>
                    );
                },
                size: 100,
            }),
            columnHelper.accessor('note', {
                id: 'note',
                header: 'الملاحظة',
                cell: ({ getValue }) => {
                    const note = getValue();
                    return note ? (
                        <span className="text-muted" style={{
                            backgroundColor: '#f8f9fa',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            display: 'inline-block',
                            fontSize: '11px'
                        }}>
                            <FaStickyNote className="mx-1" />
                            {note}
                        </span>
                    ) : (
                        <span className="text-muted">لا توجد ملاحظات</span>
                    );
                },
                size: 150,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'الحالة',
                cell: ({ getValue }) => {
                    const statusInfo = getStatusInfo(getValue());
                    return (
                        <span className={`badge ${statusInfo.class}`} style={{ color: 'white' }}>
                            {statusInfo.text}
                        </span>
                    );
                },
                filterFn: (row, id, value) => {
                    return row.getValue(id) === value;
                },
                size: 120,
            }),
            columnHelper.accessor('created_at', {
                id: 'created_at',
                header: 'التاريخ',
                cell: ({ getValue }) => {
                    const date = getValue();
                    return (
                        <small className="text-muted">
                            {date ? new Date(date).toLocaleDateString('ar-EG') : 'غير محدد'}
                        </small>
                    );
                },
                size: 100,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'الإجراءات',
                cell: ({ row }) => {
                    const request = row.original;
                    return (
                        <div className="action-buttons d-flex gap-1 flex-wrap justify-content-center">
                            <button
                                className="btn btn-sm btn-action"
                                style={{
                                    backgroundColor: '#17a2b8',
                                    borderColor: '#17a2b8',
                                    color: 'white',
                                    fontSize: '12px',
                                    padding: '4px 8px'
                                }}
                                title="عرض التفاصيل"
                                onClick={() => window.location.href = `/pharmacy-branch-requests/${request.id}`}
                            >
                                <FaEye className="mx-1" />
                                عرض
                            </button>
                        </div>
                    );
                },
                size: 100,
            }),
        ],
        [getStatusInfo]
    );

    // Table configuration
    const table = useReactTable({
        data: requests || [],
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();

            if (!token) {
                toast.error('لا يوجد token للمصادقة', { containerId: 'main-toast-container' });
                return;
            }

            const response = await axios.get(`${BASE_URL}/pharmacy/main-branch-requests?page=${currentPage}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });


            if (response.data.status === 200 || response.data.status === 'success') {
                // البيانات موجودة في response.data.data.data
                const requestsData = response.data.data;

                setRequests(requestsData.data || []);
                setPagination(requestsData);

                // تحديث pageGroup بناءً على الصفحة الحالية
                if (requestsData) {
                    const totalPages = requestsData.total_pages || requestsData.last_page || 1;
                    const pagesPerGroup = Math.ceil(totalPages / 2);
                    const currentPage = requestsData.current_page || 1;
                    const newPageGroup = Math.floor((currentPage - 1) / pagesPerGroup);
                    setPageGroup(newPageGroup);
                }
            } else {
                throw new Error(response.data.message || 'فشل في جلب طلبات الفروع');
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'فشل في جلب طلبات الفروع');
            toast.error(error.response?.data?.message || error.message || 'فشل في جلب طلبات الفروع', { containerId: 'main-toast-container' });
        } finally {
            setLoading(false);
        }
    };


    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">جاري التحميل...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="alert alert-danger">
                            <h4>خطأ</h4>
                            <p>{error}</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => window.location.reload()}
                            >
                                إعادة المحاولة
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }



    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0" style={{ fontSize: '20px' }}>طلبات الفروع</h2>
                </div>

                {/* Search and Filter Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern mb-4"
                >
                    <div className="card-body p-3">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="search-container position-relative">
                                    <div className="input-group search-input-group">
                                        <span className="input-group-text search-icon">
                                            <FaSearch className="text-white" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control search-input"
                                            placeholder="البحث في طلبات الفروع..."
                                            value={globalFilter ?? ''}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 text-end">
                                <div className="d-flex gap-2 justify-content-end align-items-center">
                                    {(globalFilter || columnFilters.length > 0) && (
                                        <span className="badge bg-info text-white mx-2">
                                            {table.getFilteredRowModel().rows.length} من {requests?.length || 0} نتيجة
                                        </span>
                                    )}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <FaFilter className="mx-1" />
                                        {showFilters ? 'إخفاء المرشحات' : 'إظهار المرشحات'}
                                    </button>
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            setGlobalFilter('');
                                            setColumnFilters([]);
                                            setSorting([]);
                                        }}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        مسح الكل
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Advanced Filters */}
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3 pt-3 border-top"
                            >
                                <div className="row">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">حالة الطلب</label>
                                        <select
                                            className="form-select"
                                            value={columnFilters.find(f => f.id === 'status')?.value ?? ''}
                                            onChange={(e) => {
                                                const statusFilter = e.target.value;
                                                setColumnFilters(prev =>
                                                    prev.filter(f => f.id !== 'status').concat(
                                                        statusFilter ? [{ id: 'status', value: statusFilter }] : []
                                                    )
                                                );
                                            }}
                                        >
                                            <option value="">جميع الحالات</option>
                                            <option value="approved">موافق عليه</option>
                                            <option value="rejected">مرفوض</option>
                                            <option value="pending">في الانتظار</option>
                                            <option value="doctor_responded">رد الصيدلية</option>
                                            <option value="under_review">قيد المراجعة</option>
                                            <option value="completed">مكتمل</option>
                                            <option value="cancelled">ملغي</option>
                                        </select>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>

                {/* Modern Data Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern"
                >
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">جاري التحميل...</span>
                                </div>
                            </div>
                        ) : !Array.isArray(requests) || requests.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="text-muted">
                                    <FaImage size={48} className="mb-3" />
                                    <p>لا توجد طلبات في الوقت الحالي</p>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table mb-0 table-modern" style={{
                                    backgroundColor: 'transparent',
                                    '--bs-table-striped-bg': 'transparent',
                                    '--bs-table-hover-bg': 'rgba(0,0,0,0.05)'
                                }}>
                                    <thead className="table-light">
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <th
                                                        key={header.id}
                                                        className="border-0 fw-semibold text-center"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                            userSelect: 'none',
                                                            width: header.getSize()
                                                        }}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                            {header.column.getCanSort() && (
                                                                <span>
                                                                    {header.column.getIsSorted() === 'asc' ? (
                                                                        <FaSortUp className="text-primary" />
                                                                    ) : header.column.getIsSorted() === 'desc' ? (
                                                                        <FaSortDown className="text-primary" />
                                                                    ) : (
                                                                        <FaSort className="text-muted" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map((row, index) => (
                                            <motion.tr
                                                key={row.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                                style={{ backgroundColor: 'transparent' }}
                                                className="table-row-hover"
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className="border-0 text-center align-middle"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            width: cell.column.getSize()
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Table Pagination with Wrap */}
                    {table.getPageCount() > 1 && (
                        <div className="card-footer bg-light">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="text-muted">الانتقال إلى الصفحة</span>
                                        <div className="input-group" style={{ width: 'auto' }}>
                                            <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                                #
                                            </span>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                value={table.getState().pagination.pageIndex + 1}
                                                onChange={(e) => {
                                                    const value = parseInt(e.target.value);
                                                    if (!isNaN(value) && value > 0 && value <= table.getPageCount()) {
                                                        table.setPageIndex(value - 1);
                                                    }
                                                }}
                                                min="1"
                                                max={table.getPageCount()}
                                                style={{ width: '60px' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-end gap-2 flex-wrap">
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(0)}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            الأولى
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.previousPage()}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            السابقة
                                        </button>
                                        <span className="d-flex align-items-center px-3">
                                            صفحة {table.getState().pagination.pageIndex + 1} من {table.getPageCount()}
                                        </span>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.nextPage()}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            التالية
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            الأخيرة
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* API Pagination */}
                {pagination && (pagination.total > 0 || requests?.length > 0) && (
                    <div className="card-footer bg-light">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="text-muted">الانتقال إلى الصفحة</span>
                                    <div className="input-group" style={{ width: 'auto' }}>
                                        <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                            #
                                        </span>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={currentPage}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                if (!isNaN(value) && value > 0) {
                                                    const totalPages = pagination?.total_pages || pagination?.last_page || 1;
                                                    if (value <= totalPages) {
                                                        handlePageChange(value);
                                                    }
                                                }
                                            }}
                                            min="1"
                                            max={pagination?.total_pages || pagination?.last_page || 1}
                                            style={{ width: '60px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2 flex-wrap">
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage <= 1}
                                    >
                                        الأولى
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                    >
                                        السابقة
                                    </button>
                                    <span className="d-flex align-items-center px-3">
                                        صفحة {pagination.current_page || pagination.currentPage || 1} من {pagination.total_pages || pagination.last_page || 1}
                                    </span>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage >= (pagination.total_pages || pagination.last_page || 1)}
                                    >
                                        التالية
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(pagination.total_pages || pagination.last_page || 1)}
                                        disabled={currentPage >= (pagination.total_pages || pagination.last_page || 1)}
                                    >
                                        الأخيرة
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PharmacyBranchRequests;

// CSS للجدول المحسن
const styles = `
.table-modern {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: 1200px;
}

.table-modern thead th {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
    padding: 1rem 0.75rem;
    position: sticky;
    top: 0;
    z-index: 10;
}

.table-modern tbody tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f1f3f4;
}

.table-modern tbody tr:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.table-modern tbody tr:last-child {
    border-bottom: none;
}

.table-modern td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    white-space: normal;
    word-wrap: break-word;
    max-width: 200px;
    min-width: 120px;
}

/* تحسين عرض اسم الفرع للشاشات الكبيرة */
.table-modern td:nth-child(3) {
    max-width: 300px;
    min-width: 200px;
    text-align: center;
}

.table-modern td:nth-child(3) .badge {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}

/* تحسين عرض عمود العنوان */
.table-modern td:nth-child(6) {
    max-width: 350px;
    min-width: 250px;
    text-align: center;
    word-wrap: break-word;
    white-space: normal;
}

.table-modern td:nth-child(6) span {
    display: inline-block;
    max-width: 100%;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.4;
}

/* تحسين عرض البيانات في الخلايا */
.table-modern td span,
.table-modern td div,
.table-modern td h6 {
    word-break: break-word;
    line-height: 1.4;
}

/* تحسين عرض النصوص الطويلة */
.table-modern td .text-muted {
    font-size: 0.9rem;
    line-height: 1.3;
}

/* تحسين عرض البادجات */
.table-modern td .badge {
    font-size: 0.75rem;
    padding: 0.4em 0.6em;
    white-space: nowrap;
}

.action-buttons .btn {
    margin: 2px;
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.action-buttons .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.card-modern {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: hidden;
}

.card-modern .card-body {
    padding: 1.5rem;
}

.table-responsive {
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
}

/* Custom Horizontal Scrollbar for Table */
.table-responsive::-webkit-scrollbar {
    height: 8px;
    width: 0;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border-radius: 4px;
    transition: all 0.3s ease;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%);
    transform: scale(1.1);
}

/* Firefox Horizontal Scrollbar */
.table-responsive {
    scrollbar-width: thin;
    scrollbar-color: #184194 #f1f1f1;
}

/* Search Input Styling */
.search-container {
    position: relative;
}

.search-input-group {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.search-input-group:focus-within {
    box-shadow: 0 4px 16px rgba(24, 65, 148, 0.2);
    transform: translateY(-1px);
}

.search-icon {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border: none;
    color: white;
    padding: 12px 16px;
    font-size: 16px;
}

.search-input {
    border: none;
    padding: 12px 16px;
    font-size: 14px;
    background: white;
    transition: all 0.3s ease;
}

.search-input:focus {
    border: none;
    box-shadow: none;
    background: #f8f9fa;
}

.search-input::placeholder {
    color: #6c757d;
    font-style: italic;
}

.input-group .form-control:focus {
    border-color: #184194;
    box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.25);
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    border-color: #6c757d;
}

.btn-outline-primary:hover {
    background-color: #184194;
    border-color: #184194;
}

.table-row-hover:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
}

/* Pagination Wrap */
.card-footer .d-flex {
    flex-wrap: wrap;
    gap: 0.5rem;
}

.card-footer .btn {
    white-space: nowrap;
}

/* Original Pagination Styling */
.pagination {
    margin-bottom: 0;
}

.pagination .page-link {
    color: #184194;
    border-color: #dee2e6;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    margin: 0 2px;
    transition: all 0.2s ease;
}

.pagination .page-link:hover {
    color: #fff;
    background-color: #184194;
    border-color: #184194;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(24, 65, 148, 0.3);
}

.pagination .page-item.active .page-link {
    background-color: #184194;
    border-color: #184194;
    color: white;
}

.pagination .page-item.disabled .page-link {
    color: #6c757d;
    background-color: #fff;
    border-color: #dee2e6;
    cursor: not-allowed;
}

/* Original Pagination Styling */
.pagination {
    margin-bottom: 0;
}

.pagination .page-link {
    color: #184194;
    border-color: #dee2e6;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    margin: 0 2px;
    transition: all 0.2s ease;
}

.pagination .page-link:hover {
    color: #fff;
    background-color: #184194;
    border-color: #184194;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(24, 65, 148, 0.3);
}

.pagination .page-item.active .page-link {
    background-color: #184194;
    border-color: #184194;
    color: white;
}

/* Pagination Wrap */
.pagination {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem;
}

.pagination .page-item {
    margin: 0.125rem;
}

/* Page Input Navigation Styling */
.page-input-container {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    padding: 0.75rem 1rem;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    backdrop-filter: blur(10px);
}

.page-input-form {
    background: transparent;
    padding: 0;
    border: none;
    box-shadow: none;
}

.page-input-custom {
    border: 2px solid #e9ecef !important;
    border-radius: 8px !important;
    text-align: center !important;
    font-weight: 600 !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
    transition: all 0.3s ease !important;
}

.page-input-custom:focus {
    border-color: #184194 !important;
    box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.25), 0 4px 8px rgba(24, 65, 148, 0.15) !important;
    transform: translateY(-1px) !important;
    background: #ffffff !important;
}

.page-input-icon {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 12px;
    pointer-events: none;
}

.page-go-btn {
    border-radius: 8px !important;
    font-weight: 600 !important;
    transition: all 0.3s ease !important;
    position: relative;
    overflow: hidden;
}

.page-go-btn:hover:not(:disabled) {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 12px rgba(24, 65, 148, 0.4) !important;
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%) !important;
}

.page-go-btn:active:not(:disabled) {
    transform: translateY(0) !important;
    box-shadow: 0 2px 4px rgba(24, 65, 148, 0.3) !important;
}

.page-go-btn:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
    transform: none !important;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}

.page-go-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
}

.page-go-btn:hover::before {
    left: 100%;
}

@media (max-width: 768px) {
    .action-buttons {
        flex-direction: column;
        gap: 4px;
    }
    
    .action-buttons .btn {
        width: 100%;
        font-size: 11px;
        padding: 4px 6px;
    }
    
    .table-modern {
        min-width: 800px;
    }
    
    .table-modern th,
    .table-modern td {
        padding: 0.5rem 0.25rem;
        font-size: 0.875rem;
        max-width: 150px;
        min-width: 80px;
    }
    
    .table-modern td .text-muted {
        font-size: 0.8rem;
    }
    
    .table-modern td .badge {
        font-size: 0.7rem;
        padding: 0.3em 0.5em;
    }
    
    /* تحسين عرض اسم الفرع */
    .table-modern td:nth-child(3) {
        max-width: 300px;
        min-width: 200px;
        text-align: center;
    }
    
    .table-modern td:nth-child(3) .badge {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: inline-block;
    }
    
    /* تحسين عرض عمود العنوان للشاشات الصغيرة */
    .table-modern td:nth-child(6) {
        max-width: 250px;
        min-width: 200px;
        text-align: center;
        word-wrap: break-word;
        white-space: normal;
    }
    
    .search-input-group {
        margin-bottom: 1rem;
    }
    
    .search-input {
        font-size: 16px; /* منع التكبير في iOS */
    }
    
    .card-footer .d-flex {
        justify-content: center !important;
    }
    
    .card-footer .col-md-6 {
        text-align: center;
    }
    
    .pagination {
        justify-content: center;
        flex-wrap: wrap;
    }
    
    .pagination .page-link {
        padding: 0.375rem 0.5rem;
        font-size: 0.875rem;
    }
    
    .pagination {
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.25rem;
    }
    
    .pagination .page-item {
        margin: 0.125rem;
    }
    
    .table-responsive {
        overflow-x: auto;
        overflow-y: hidden;
    }
    
    .table-responsive::-webkit-scrollbar {
        height: 6px;
        width: 0;
    }
    
    .table-responsive::-webkit-scrollbar-thumb {
        background: #184194;
        border-radius: 3px;
    }
}
`;

// إضافة الـ CSS للصفحة
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}
